'use client';

import React, { useEffect, useRef } from 'react';

const therapeuticAreas = [
  {
    id: 'ANTI-INFECTIVE',
    label: 'Anti-Infective Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2"/>
        <path d="M10 16C10 16 12 12 16 12C20 12 22 16 22 16C22 16 20 20 16 20C12 20 10 16 10 16Z" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="16" cy="16" r="3" fill="currentColor" fillOpacity="0.3"/>
        <path d="M16 4V8M16 24V28M4 16H8M24 16H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'RESPIRATORY',
    label: 'Respiratory Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 6C16 6 16 10 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M16 14C14 14 8 14 6 18C4 22 6 28 10 28C12 28 14 26 16 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M16 14C18 14 24 14 26 18C28 22 26 28 22 28C20 28 18 26 16 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M14 6H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'GASTROINTESTINAL',
    label: 'Gastrointestinal Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M8 8C8 8 6 12 8 16C10 20 8 24 10 26C12 28 16 28 18 26C20 24 22 20 20 16C18 12 20 8 18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M18 6C20 4 24 4 24 8C24 12 20 14 18 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="2" fill="currentColor" fillOpacity="0.3"/>
      </svg>
    ),
  },
  {
    id: 'PAIN-INFLAMMATION',
    label: 'Pain & Inflammation',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 4L19 13H28L21 19L24 28L16 22L8 28L11 19L4 13H13L16 4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'ANTI-ALLERGIC',
    label: 'Anti-Allergic Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 16L14.5 18.5L20 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 6V4M16 28V26M6 16H4M28 16H26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'ANTISPASMODIC',
    label: 'Antispasmodic Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M6 16C8 12 12 10 16 12C20 14 24 12 26 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M6 22C8 18 12 16 16 18C20 20 24 18 26 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="16" cy="15" r="3" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    id: 'LIVER-CARE',
    label: 'Liver Care',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M6 14C6 10 9 7 13 7C15 7 16 8 16 8C16 8 17 7 19 7C23 7 26 10 26 14C26 20 20 26 16 26C12 26 6 20 6 14Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        <path d="M16 8V14M13 14H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'PROBIOTIC',
    label: 'Probiotic / Women\'s Health',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="10" r="6" stroke="currentColor" strokeWidth="2"/>
        <path d="M16 16V22M12 26H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="8" cy="16" r="3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
        <circle cx="24" cy="16" r="3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
      </svg>
    ),
  },
];

export default function TherapeuticAreas() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleAreaClick = (id: string) => {
    const el = document.getElementById('products');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const filterEvent = new CustomEvent('filterCategory', { detail: id });
        window.dispatchEvent(filterEvent);
      }, 600);
    }
  };

  return (
    <section id="therapeutic-areas" ref={sectionRef} className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 space-y-3">
          <div className="section-reveal stagger-1">
            <span className="section-label">Our Expertise</span>
          </div>
          <h2 className="section-reveal stagger-2 font-display text-foreground leading-tight" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
            Therapeutic Areas
          </h2>
          <p className="section-reveal stagger-3 text-muted-foreground max-w-xl mx-auto leading-relaxed">
            A comprehensive pharmaceutical portfolio spanning eight key therapeutic segments.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {therapeuticAreas.map((area, i) => (
            <button
              key={area.id}
              onClick={() => handleAreaClick(area.id)}
              className={`therapeutic-card text-left section-reveal stagger-${Math.min(i + 2, 8)}`}
            >
              <div className="w-12 h-12 bg-blue-50 text-primary rounded-xl flex items-center justify-center mb-3 transition-colors group-hover:bg-primary group-hover:text-white">
                {area.icon}
              </div>
              <h3 className="font-semibold text-foreground text-sm leading-snug">{area.label}</h3>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}